
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { Logo } from '@/components/ui/logo';
import { CircleDecor } from '@/components/ui/visual-elements';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';

const resetSchema = z.object({
  email: z.string().email('Digite um email válido').min(1, 'Email é obrigatório'),
});

type ResetFormValues = z.infer<typeof resetSchema>;

const ForgotPassword = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: '',
    }
  });
  
  const onSubmit = async (values: ResetFormValues) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: window.location.origin + '/redefinir-senha',
      });
      
      if (error) {
        throw error;
      }
      
      setSubmitted(true);
      toast({
        title: "Email enviado",
        description: "Instruções para redefinir sua senha foram enviadas para seu email.",
      });
    } catch (error: any) {
      console.error("Erro ao enviar email de redefinição:", error.message);
      toast({
        title: "Erro ao enviar email",
        description: error.message || "Não foi possível enviar o email de redefinição. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-hidden">
      <CircleDecor className="absolute -top-10 -right-10 opacity-30" size="lg" />
      <CircleDecor className="absolute bottom-10 left-10 opacity-30" variant="secondary" />
      
      <div className="flex flex-col items-center justify-center w-full p-8 relative z-10">
        <div className="text-center mb-8">
          <Logo className="mx-auto mb-4" size="lg" />
          <p className="text-gray-600">recuperar senha</p>
        </div>
        
        <div className="w-full max-w-md">
          {!submitted ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="seu@email.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-brand-blue hover:bg-opacity-90 text-white"
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "recuperar senha"}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="text-center">
              <p className="mb-4">
                Se uma conta existir com este email, enviaremos instruções para redefinir sua senha.
              </p>
              <p>
                Verifique seu email e siga as instruções enviadas.
              </p>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-brand-blue hover:underline">
              voltar para login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
